/**
 * Ticket detail — a plain, reusable detail sheet for one ticket. Its own root
 * IS its content (no `show()`, no chrome of its own), so a caller mounts it
 * inside whatever wrapper or overlay it already has.
 *
 * Two jobs, one surface: a confirm-before-write brake (`pending`, Admit / Not
 * this time) for a caller carrying no gate of its own — the door's own scan
 * surface reaches for `pending-admit-view` instead — and the read-only "see
 * it again" pull-up for a dispute right after a scan (informational, Close
 * only). Props in, events out: the two jobs draw from different data sources
 * — the pending case from verdict.state.ts's `pending-admit` verdict, the
 * informational case from wherever a caller reads a resolved ticket's own
 * status — so this component owns neither and stays a plain, reusable detail
 * sheet.
 *
 * Carries exactly one person-identifying value, `holder`: the name the buyer
 * typed for whoever they handed this ticket to. Whose ticket this is is a
 * question the door has, and this sheet is where a ticket's own facts are
 * read, so the name belongs here. It arrives as a property like every other
 * fact on this sheet, resolved by the caller from the ticket's one live
 * `Handover` (packages/types/src/handover.entity.ts) — a sibling collection
 * under the event, never a field on Ticket, whose whole document the scan
 * role reads and lists. Nothing else about a person reaches this file: no
 * buyer name, no address, no contact detail, no order reference; and the real
 * Ticket entity mints no human-facing display code, so nothing here stands in
 * for one.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { translate } from "@momo/i18n";
import { html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { ticketTypeName } from "../ticket-types/ticket-types.state.ts";
import type { PartyContext, ResolvedTicket } from "./verdict.state.ts";

@schmancyElement("ticket-sheet-view")
export class TicketSheetView extends LocalizeMixin(SchmancyElement) {
	@property({ attribute: false }) ticket!: ResolvedTicket;
	@property({ attribute: false }) party: PartyContext | null = null;
	/** The name from this ticket's one live handover, or `null` — the ordinary
	 *  case, since most tickets were never handed to anyone. A late-arriving
	 *  fact the caller resolves separately from the ticket, so a sheet whose
	 *  caller has no answer yet, or never gets one, simply renders no row. */
	@property({ attribute: false }) holder: string | null = null;
	@property({ type: Boolean }) pending = false;
	@property({ attribute: false }) status:
		| "valid"
		| "already-in-self"
		| "already-in-other" = "valid";

	override render() {
		const status = this.pending
			? {
					tone: "text-success-default",
					icon: "pending",
					key: "scanner.ticketSheet.pendingBadge" as const,
				}
			: this.status === "already-in-self"
				? {
						tone: "text-warning-default",
						icon: "how_to_reg",
						key: "scanner.ticketSheet.alreadyInSelfBadge" as const,
					}
				: this.status === "already-in-other"
					? {
							tone: "text-warning-default",
							icon: "how_to_reg",
							key: "scanner.ticketSheet.alreadyInOtherBadge" as const,
						}
					: {
							tone: "text-success-default",
							icon: "check_circle",
							key: "scanner.ticketSheet.admittedBadge" as const,
						};

		return html`
			<div class="flex max-w-md flex-col gap-4 p-4">
				<schmancy-surface
					type="container"
					opacity="translucent"
					class="flex flex-col gap-2 overflow-hidden rounded-tl-2xl rounded-tr-2xl rounded-br-bezel p-5"
				>
					<schmancy-typography type="headline" token="lg">${ticketTypeName(this.ticket.typeId, this.uiLanguage) ?? ""}</schmancy-typography>
					<schmancy-typography type="label" token="md" class=${this.classMap({ "flex items-center gap-2": true, [status.tone]: true })}>
                        <schmancy-icon>${status.icon}</schmancy-icon> ${translate(this.uiLanguage, status.key)}
					</schmancy-typography>
				</schmancy-surface>

				${when(
					this.holder !== null,
					() => html`
						<div class="flex items-center gap-2 px-1">
							<schmancy-icon class="text-surface-onVariant">redeem</schmancy-icon>
							<schmancy-typography type="title" token="sm">
								${translate(this.uiLanguage, "scanner.verdict.holder", { name: this.holder ?? "" })}
							</schmancy-typography>
						</div>
					`,
				)}

				${when(this.party, (party?: PartyContext | null) =>
					party
						? html`
									<div class="flex items-center gap-2 px-1">
										<schmancy-icon class="text-surface-onVariant">groups</schmancy-icon>
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
											${translate(
												this.uiLanguage,
												"scanner.ticketSheet.party",
												{
													total: party.total,
													alreadyIn: party.othersAlreadyIn,
													remaining: party.othersRemaining,
												},
											)}
										</schmancy-typography>
									</div>
								`
						: nothing,
				)}

				<schmancy-surface type="containerLow" opacity="translucent" elevated class="sticky bottom-0 rounded-t-2xl p-4">
					${
						this.pending
							? html`
									<!-- Vertical stack, not a side-by-side pair: Admit is the
									     expected path (most scans ARE legitimate) and earns the
									     full-width filled emphasis; decline stays a quiet text
									     button beneath it — VARIANT_EXPRESSES_EMPHASIS_RANK, and
									     the only layout that stays legible on a real phone's
									     narrow width without truncating either label. -->
									<div class="flex flex-col gap-2">
										<schmancy-button
											variant="filled"
											width="full"
											@click=${() =>
												this.dispatchEvent(
													new CustomEvent("confirm-admit", {
														bubbles: true,
														composed: true,
														detail: this.ticket,
													}),
												)}
										>
											<schmancy-icon slot="prefix">check_circle</schmancy-icon>
											${translate(this.uiLanguage, "scanner.ticketSheet.admit")}
										</schmancy-button>
										<schmancy-button
											variant="text"
											width="full"
											@click=${() => this.dispatchEvent(new CustomEvent("reject", { bubbles: true, composed: true }))}
										>
											${translate(this.uiLanguage, "scanner.ticketSheet.reject")}
										</schmancy-button>
									</div>
								`
							: html`
									<schmancy-button
										variant="filled"
										width="full"
										@click=${() => this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }))}
									>
										${translate(this.uiLanguage, "scanner.ticketSheet.close")}
									</schmancy-button>
								`
					}
				</schmancy-surface>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"ticket-sheet-view": TicketSheetView;
	}
}
